import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAuthUser } from 'auth/hooks';
import { IConfirmation } from 'core/types';
import { IPricingProfile } from 'pricing/types';
import {
  QuoteEntityType,
  QuotePartListSelectionAction,
  QuotePartListTriggerAction,
} from 'quotes/enums';
import {
  ICustomerQuote,
  IProjectSubmitBlock,
  IProjectSubmitWarning,
  IQuotePendingCliJobs,
} from 'quotes/types';

import { SubmitCustomerOrderButton } from './Actions';
import QuotePartListTableView from './QuotePartListTableView';
import { useActions } from './useActions';
import { useCheckInventoryPendingCliJobs } from './useCheckInventoryPendingCliJob';
import { useCheckVersioningPendingCliJobs } from './useCheckVersioningPendingCliJobs';
import { useSelectionActions } from './useSelectionActions';
import { isSubmitOrderVisible } from '../quoteStatus';

interface IProps {
  quote: ICustomerQuote;
  updateQuote: (updatedQuote?: ICustomerQuote) => void;
  quoteEntityType?: QuoteEntityType;
  pendingCliJobs?: IQuotePendingCliJobs;
  projectSubmitBlock?: IProjectSubmitBlock | null;
  projectSubmitWarning?: IProjectSubmitWarning | null;
}

const QuotePartListTable = ({
  quote,
  updateQuote,
  quoteEntityType = QuoteEntityType.QUOTE,
  pendingCliJobs = null,
  projectSubmitBlock = null,
  projectSubmitWarning = null,
}: IProps) => {
  const [confirmationOption, setConfirmationOption] = useState<IConfirmation | null>(null);
  const [defaultPricingProfile, setDefaultPricingProfile] = useState<IPricingProfile | null>(null);
  const [trigger, setTrigger] = useState<QuotePartListTriggerAction | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const { user, isFetched } = useAuthUser();

  useEffect(() => {
    if (!isFetched) {
      return;
    }

    if (!searchParams.has('trigger')) {
      setTrigger(null);

      return;
    }

    setTrigger(searchParams.get('trigger') as QuotePartListTriggerAction);
    setSearchParams([]);
  }, [isFetched, searchParams, setSearchParams]);

  useCheckVersioningPendingCliJobs({
    initDraftCliJob: pendingCliJobs?.initDraft,
    releaseDraftCliJob: pendingCliJobs?.releaseDraft,
    resetDraftCliJob: pendingCliJobs?.resetDraft,
  });

  useCheckInventoryPendingCliJobs({
    fetchExternalInventoryCliJob: pendingCliJobs?.fetchExternalInventory,
  });

  const { activeActionDrawer, setActiveActionDrawer, onCloseActionDrawer, actions } = useActions(
    quote,
    user,
    setConfirmationOption,
    quoteEntityType,
    trigger
  );

  const {
    activeSelectionActionDrawer,
    setActiveSelectionActionDrawer,
    closeSelection,
    onCloseSelectionActionDrawer,
    selectionActions,
    setCloseSelection,
  } = useSelectionActions(quote, user?.companySettings, setConfirmationOption, quoteEntityType);

  const onPricingProfileFormClose = () => {
    setActiveSelectionActionDrawer(QuotePartListSelectionAction.ADD_TO_PRICING_PROFILE);
  };

  const onPricingProfileSubmitSuccess = (pricingProfile: IPricingProfile) => {
    setDefaultPricingProfile(pricingProfile);
  };

  const actionButtons = useMemo(() => {
    const buttons = [];

    if (isSubmitOrderVisible(quoteEntityType, quote.status)) {
      buttons.push(
        <SubmitCustomerOrderButton
          key="submitOrder"
          quote={quote}
          onUpdateQuote={updateQuote}
          quoteEntityType={quoteEntityType}
          onSubmitSuccess={updateQuote}
          projectSubmitBlock={projectSubmitBlock}
          projectSubmitWarning={projectSubmitWarning}
        />
      );
    }

    return buttons;
  }, [projectSubmitBlock, projectSubmitWarning, quote, quoteEntityType, updateQuote]);

  return (
    <QuotePartListTableView
      quote={quote}
      actions={actions}
      actionButtons={actionButtons}
      activeActionDrawer={activeActionDrawer}
      setActiveActionDrawer={setActiveActionDrawer}
      onCloseActionDrawer={onCloseActionDrawer}
      selectionActions={selectionActions}
      activeSelectionActionDrawer={activeSelectionActionDrawer}
      setActiveSelectionActionDrawer={setActiveSelectionActionDrawer}
      onCloseSelectionActionDrawer={onCloseSelectionActionDrawer}
      closeSelection={closeSelection}
      setCloseSelection={setCloseSelection}
      confirmationOption={confirmationOption}
      setConfirmationOption={setConfirmationOption}
      defaultPricingProfile={defaultPricingProfile}
      setDefaultPricingProfile={setDefaultPricingProfile}
      onPricingProfileFormClose={onPricingProfileFormClose}
      onPricingProfileSubmitSuccess={onPricingProfileSubmitSuccess}
      quoteEntityType={quoteEntityType}
    />
  );
};

export default QuotePartListTable;
